import { LRUCache } from "lru-cache";
import type { ComponentDef } from "#gen/pages/v2/component_pb.js";
import type { Component } from "#gen/pages/v2/page_pb.js";
import type { RegistryClient } from "#lib/api/registry.js";
import Checkout from "#lib/components/flow/editor/content/commerce/Checkout.svelte";
import ProductRecommendations from "#lib/components/flow/editor/content/commerce/Recommendations.svelte";
import ProductRecommendationsEditor from "#lib/components/flow/editor/content/commerce/RecommendationsEditor.svelte";
import type { ComponentEditingProps, ComponentProps } from "#lib/components/page/ComponentProps.js";
import Avatar from "#lib/components/page/components/Avatar.svelte";
import AvatarEditor from "#lib/components/page/components/AvatarEditor.svelte";
import Box from "#lib/components/page/components/Box.svelte";
import BoxEditor from "#lib/components/page/components/BoxEditor.svelte";
import Button from "#lib/components/page/components/Button.svelte";
import ButtonEditor from "#lib/components/page/components/ButtonEditor.svelte";
import Card from "#lib/components/page/components/Card.svelte";
import CardEditor from "#lib/components/page/components/CardEditor.svelte";
import ContactInformation from "#lib/components/page/components/ContactInformation.svelte";
import ContactInformationEditor from "#lib/components/page/components/ContactInformationEditor.svelte";
import Form from "#lib/components/page/components/Form.svelte";
import FormEditor from "#lib/components/page/components/FormEditor.svelte";
import Header from "#lib/components/page/components/Header.svelte";
import HeaderEditor from "#lib/components/page/components/HeaderEditor.svelte";
import ImageButton from "#lib/components/page/components/ImageButton.svelte";
import ImageButtonEditor from "#lib/components/page/components/ImageButtonEditor.svelte";
import Img from "#lib/components/page/components/Img.svelte";
import ImgEditor from "#lib/components/page/components/ImgEditor.svelte";
import Poll from "#lib/components/page/components/Poll.svelte";
import PollEditor from "#lib/components/page/components/PollEditor.svelte";
import PropertyList from "#lib/components/page/components/PropertyList.svelte";
import PropertyListEditor from "#lib/components/page/components/PropertyListEditor.svelte";
import SocialLinks from "#lib/components/page/components/SocialLinks.svelte";
import SocialLinksEditor from "#lib/components/page/components/SocialLinksEditor.svelte";
import Text from "#lib/components/page/components/Text.svelte";
import TextEditor from "#lib/components/page/components/TextEditor.svelte";
import VideoEmbed from "#lib/components/page/components/VideoEmbed.svelte";
import VideoEmbedEditor from "#lib/components/page/components/VideoEmbedEditor.svelte";
import type { JsonObject } from "@bufbuild/protobuf";
import type { Component as SvelteComponent } from "svelte";

const DEFINITIONS_CACHE_KEY = "__definitions__";
const TTL = 1000 * 60 * 5;
const cache: LRUCache<string, JsonObject | string> = new LRUCache({ ttl: TTL, max: 500, ttlAutopurge: true });
const definitions: LRUCache<string, ComponentDefMap> = new LRUCache({ ttl: TTL, max: 500, ttlAutopurge: true });

const components: Record<string, SvelteComponent<ComponentProps>> = {
	Avatar,
	Box,
	Button,
	Card,
	Checkout,
	ContactInformation,
	Form,
	Header,
	ImageButton,
	Img,
	Poll,
	ProductRecommendations,
	PropertyList,
	SocialLinks,
	Text,
	VideoEmbed,
};

const editorComponents: Record<string, SvelteComponent<ComponentEditingProps>> = {
	AvatarEditor,
	BoxEditor,
	ButtonEditor,
	CardEditor,
	ContactInformationEditor,
	FormEditor,
	HeaderEditor,
	ImageButtonEditor,
	ImgEditor,
	PollEditor,
	ProductRecommendationsEditor,
	PropertyListEditor,
	SocialLinksEditor,
	TextEditor,
	VideoEmbedEditor,
};

export type ComponentDefMap = Record<string, ComponentDef>;

export class Registry {
	private client: RegistryClient;
	private definitions: ComponentDefMap = {};

	constructor(client: RegistryClient) {
		this.client = client;
	}

	private collectTypeURIs(component: Component, typeURIs: string[]) {
		typeURIs.push(component.typeUri);
		component.children.forEach(component => this.collectTypeURIs(component, typeURIs));
	}

	async preload(): Promise<void> {
		this.definitions = definitions.get(DEFINITIONS_CACHE_KEY) as ComponentDefMap;
		if (!this.definitions) {
			return this.client.getAllComponents().then(result => {
				this.definitions = result.reduce((acc, definition) => {
					acc[definition.typeUri] = definition;
					return acc;
				}, {} as ComponentDefMap);
				definitions.set(DEFINITIONS_CACHE_KEY, this.definitions);
			});
		}
	}

	getDefinitions(): ComponentDefMap {
		return this.definitions;
	}

	getDefinition(typeUri: string): ComponentDef | undefined {
		return this.definitions[typeUri];
	}

	getComponent(definition: ComponentDef): SvelteComponent<ComponentProps> | undefined {
		return components[definition.componentType];
	}

	getEditorComponent(definition: ComponentDef): SvelteComponent<ComponentEditingProps> | undefined {
		return editorComponents[definition.editorComponentType];
	}

	lookupTypeUri(name: string, version: string): string | undefined {
		return cache.get(`${name}:${version}`) as string;
	}
}
